import { Badge, type BadgeProps } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from "@/components/ui/dialog";
import { PII_MASK_CLASS } from "@/lib/analytics";
import { handleActionError } from "@/lib/errors";
import {
  CLAIM_EMAIL_IN_USE_MESSAGE,
  canClaimSupplierMatch,
  collectSupplierMatchEmails,
  getSupplierMatchStatusDisplay,
  hasSupplierMatchClaimOnFile,
  isSupplierMatchClaimView
} from "@/lib/supplier-match-status";
import { useProfile } from "@/queries/customer/customer";
import {
  useClaimSupplierMatch,
  useRefreshSupplierMatchesOnOpen,
  useReleaseSupplierMatch,
  useRequestSupplierMatchEmail,
  useRetrySupplierMatch,
  useSupplierMatches,
  useSupplierMatchSellerState
} from "@/queries/customer/supplier-match";
import type { SalonStockerHttpTypes } from "@repo/api-sdk";
import { cn } from "@repo/design-system/utils";
import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type HTMLAttributes
} from "react";

import { SupplierMatchEmailForm } from "./SupplierMatchEmailForm";

interface SupplierMatchDetailDialogProps {
  /** The card being inspected, or `null` while the dialog is closed. */
  view: SalonStockerHttpTypes.StoreSupplierMatchStatusView | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

function StatusRow({
  variant,
  label
}: {
  variant: NonNullable<BadgeProps["variant"]>;
  label: string;
}) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-sm text-gray-500 dark:text-gray-400">Status</span>
      <Badge variant={variant}>{label}</Badge>
    </div>
  );
}

/** "Check <email> for a confirmation email to <finish>" - the email is masked (ADR-0006). */
function PendingEmailNotice({
  email,
  finish
}: {
  email: string;
  finish: string;
}) {
  return (
    <div className="rounded-md border border-orange-200 bg-orange-50 p-3 dark:border-orange-900/40 dark:bg-orange-900/20">
      <p className="text-sm text-orange-800 dark:text-orange-300">
        Check <span className={PII_MASK_CLASS}>{email}</span> for a confirmation
        email to {finish}
      </p>
    </div>
  );
}

/** The red notice box shared by the blocked-claim message and the release confirmation. */
function RedNotice({
  className,
  children,
  ...props
}: HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={cn(
        "rounded-md border border-red-200 bg-red-50 p-3 dark:border-red-900/40 dark:bg-red-900/20",
        className
      )}
      {...props}
    >
      {children}
    </div>
  );
}

/**
 * The inline "are you sure" row for Release/Remove. Focus moves to Cancel on
 * mount: the button that opened it unmounts, which would otherwise drop focus
 * to the dialog body. (Returning focus when it closes is the dialog's job.)
 */
function ReleaseConfirmation({
  prompt,
  confirmLabel,
  pendingLabel,
  isPending,
  onCancel,
  onConfirm
}: {
  prompt: string;
  confirmLabel: string;
  pendingLabel: string;
  isPending: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  const cancelRef = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    cancelRef.current?.focus();
  }, []);

  return (
    <RedNotice className="flex items-center justify-between gap-2">
      <p className="text-sm text-red-700 dark:text-red-400">{prompt}</p>
      <div className="flex flex-shrink-0 gap-2">
        <Button
          ref={cancelRef}
          type="button"
          variant="outline"
          size="sm"
          onClick={onCancel}
        >
          Cancel
        </Button>
        <Button
          type="button"
          variant="destructive"
          size="sm"
          onClick={onConfirm}
          disabled={isPending}
        >
          {isPending ? pendingLabel : confirmLabel}
        </Button>
      </div>
    </RedNotice>
  );
}

/**
 * A seller's Supplier Match detail - full status, the email it resolves
 * through, and the self-serve actions. Two flows share the dialog:
 *
 * - **Lookup** (Retry, Release, an alternate email for Case B).
 *   `!lookup_capable` disables every action but still shows the last-known
 *   status (ADR-0005 "Supplier scope").
 * - **Trade-account claim** (KAN-322): on a seller with no lookup
 *   integration the salon declares the account under an email, prefilled
 *   with the login email, and can Remove it or use a different email. A
 *   claim is never looked up, so there is no Retry.
 */
export function SupplierMatchDetailDialog({
  view,
  open,
  onOpenChange
}: SupplierMatchDetailDialogProps) {
  const sellerId = view?.seller_id ?? "";
  const sellerState = useSupplierMatchSellerState(sellerId, {
    enabled: open && !!sellerId
  });
  const profile = useProfile();
  // Already loaded for the grid behind the dialog, so suggestions cost no request.
  const grid = useSupplierMatches();
  const retryMatch = useRetrySupplierMatch();
  // The card behind the dialog may be from a grid loaded long ago.
  useRefreshSupplierMatchesOnOpen(open);
  const releaseMatch = useReleaseSupplierMatch();
  const requestEmail = useRequestSupplierMatchEmail();
  const claimMatch = useClaimSupplierMatch();

  const [confirmingRelease, setConfirmingRelease] = useState(false);
  const [emailFormOpen, setEmailFormOpen] = useState(false);
  const [requestError, setRequestError] = useState<string | null>(null);

  // Reset transient UI state whenever a (possibly different) seller's dialog opens.
  useEffect(() => {
    if (open) {
      setConfirmingRelease(false);
      setEmailFormOpen(false);
      setRequestError(null);
    }
  }, [open, sellerId]);

  // Focus management around the Remove/Release confirmation. Opening it
  // unmounts the trigger (ReleaseConfirmation takes focus itself); closing it
  // unmounts the confirmation, so focus goes back to the trigger. A completed
  // release usually removes the trigger too (nothing left to remove), so focus
  // then goes to the primary action that replaces it, else the dialog title.
  const releaseTriggerRef = useRef<HTMLButtonElement>(null);
  const primaryActionRef = useRef<HTMLButtonElement>(null);
  const titleRef = useRef<HTMLHeadingElement>(null);
  const wasConfirmingRef = useRef(false);
  const releasedRef = useRef(false);
  useEffect(() => {
    if (
      wasConfirmingRef.current &&
      !confirmingRelease &&
      !releasedRef.current
    ) {
      releaseTriggerRef.current?.focus();
    }
    wasConfirmingRef.current = confirmingRelease;
  }, [confirmingRelease]);
  // Runs every render on purpose: the trigger only disappears once the grid
  // refetch lands, some renders after the release succeeded.
  useEffect(() => {
    if (
      releasedRef.current &&
      !confirmingRelease &&
      !releaseTriggerRef.current
    ) {
      releasedRef.current = false;
      (primaryActionRef.current ?? titleRef.current)?.focus();
    }
  });

  // Prefer the dedicated seller-state read once it lands (the freshest source
  // right after a mutation); fall back to the grid card's own fields while it
  // loads, so the dialog never flashes empty on open.
  // `null` from the state read is an answer (nothing pending), not a gap, so
  // the grid's possibly stale value is used only while the read is loading.
  const verifiedEmail = sellerState.data
    ? sellerState.data.verified_email
    : (view?.verified_email ?? null);
  const pendingEmail = sellerState.data
    ? sellerState.data.pending_email
    : (view?.pending_email ?? null);

  // Suggestions for the email fields: the emails the salon already used,
  // minus the one a field replaces (suggesting it would be a no-op). Memoised
  // so the memoised form is not re-rendered by a fresh array every render.
  const loginEmail = profile.data?.customer?.email ?? "";
  const suggestions = useMemo(
    () =>
      collectSupplierMatchEmails(grid.data?.supplier_matches ?? [], loginEmail),
    [grid.data, loginEmail]
  );
  // "Update email" on a claim replaces the claimed email.
  const claimExcludes = useMemo(
    () => (verifiedEmail ? [verifiedEmail] : []),
    [verifiedEmail]
  );
  // A lookup alternate email replaces the login email, or the verified
  // override already in place.
  const lookupExcludes = useMemo(
    () => [loginEmail, verifiedEmail ?? ""].filter(Boolean),
    [loginEmail, verifiedEmail]
  );

  if (!view) {
    return null;
  }

  const isClaim = isSupplierMatchClaimView(view);
  const disabled = !view.lookup_capable;
  // `claim_blocked` and the email it blocked come from the grid snapshot
  // (`view`); the state read refetches on its own. If the state already shows
  // a different pending email, the salon has made a newer request and the
  // snapshot's blocked claim is stale until the grid catches up, so it is not
  // shown. This one value drives the blocked message, the pill and whether
  // the confirmation notice is hidden, so they cannot disagree.
  const claimBlocked =
    view.claim_blocked && !(pendingEmail && pendingEmail !== view.pending_email)
      ? view.claim_blocked
      : null;
  // The badge reads the same fresher pending email as the notice below, so
  // "Pending verification" and "Check <email> ..." never disagree.
  const { variant, label } = getSupplierMatchStatusDisplay({
    ...view,
    pending_email: pendingEmail,
    claim_blocked: claimBlocked
  });

  // A claim already on file (standing or awaiting its click) offers a
  // different email; the first claim is "I have an account". Only the login
  // email is ever pre-filled, and only for a first claim.
  const hasClaimOnFile = hasSupplierMatchClaimOnFile(view);
  const canChangeClaimEmail = hasClaimOnFile && view.claimable;

  const handleRequestEmail = async (email: string) => {
    setRequestError(null);
    try {
      await requestEmail.mutateAsync({ sellerId: view.seller_id, email });
      setEmailFormOpen(false);
    } catch (err) {
      setRequestError(
        handleActionError(err, {
          boundary: "supplier match request email",
          fallback: "We couldn't send your request. Please try again."
        })
      );
    }
  };

  const handleClaim = async (email: string) => {
    setRequestError(null);
    try {
      await claimMatch.mutateAsync({ sellerId: view.seller_id, email });
      setEmailFormOpen(false);
    } catch (err) {
      setRequestError(
        handleActionError(err, {
          boundary: "supplier match claim",
          fallback: "We couldn't save your account. Please try again."
        })
      );
    }
  };

  const cancelRelease = () => setConfirmingRelease(false);
  const confirmRelease = () =>
    releaseMatch.mutate(view.seller_id, {
      onSuccess: () => {
        releasedRef.current = true;
        setConfirmingRelease(false);
      }
    });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle ref={titleRef} tabIndex={-1} className="outline-none">
            {view.seller_name}
          </DialogTitle>
        </DialogHeader>

        <div className="flex flex-col gap-4">
          <StatusRow variant={variant} label={label} />

          {claimBlocked ? (
            // A standing state, not an event: `status`, so it never competes
            // with the `alert` of a fresh claim error.
            <RedNotice role="status">
              <p className="text-sm text-red-700 dark:text-red-400">
                {CLAIM_EMAIL_IN_USE_MESSAGE}
              </p>
              {/* Above "Account email <old>" the sentence alone reads as if
                  the claimed address were the one in use, so name the one
                  that collided (the API leaves it in `pending_email`). */}
              {view.status === "claimed" && view.pending_email ? (
                <p className="mt-1 text-sm text-red-700 dark:text-red-400">
                  The new email{" "}
                  <span className={PII_MASK_CLASS}>{view.pending_email}</span>{" "}
                  could not be added. Your claim stays on your current email.
                </p>
              ) : null}
            </RedNotice>
          ) : null}

          {isClaim ? (
            <>
              {view.status === "claimed" && verifiedEmail ? (
                <div>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    Account email
                  </p>
                  <p
                    className={`text-sm font-medium text-gray-900 dark:text-white ${PII_MASK_CLASS}`}
                  >
                    {verifiedEmail}
                  </p>
                </div>
              ) : null}

              {/* Not when blocked: the collision spent that email's link. */}
              {pendingEmail && !claimBlocked ? (
                <PendingEmailNotice
                  email={pendingEmail}
                  finish={
                    view.status === "claimed"
                      ? "finish updating your account. Your current email stays claimed until then."
                      : "finish claiming your account."
                  }
                />
              ) : null}

              {canClaimSupplierMatch(view) && !hasClaimOnFile ? (
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Already have a trade account with this supplier? Tell us the
                  email you use with them.
                </p>
              ) : null}

              {hasClaimOnFile && !confirmingRelease ? (
                <div className="flex gap-2">
                  <Button
                    ref={releaseTriggerRef}
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => setConfirmingRelease(true)}
                  >
                    Remove
                  </Button>
                </div>
              ) : null}

              {confirmingRelease ? (
                <ReleaseConfirmation
                  prompt="Remove your account from this supplier?"
                  confirmLabel="Yes, remove"
                  pendingLabel="Removing…"
                  isPending={releaseMatch.isPending}
                  onCancel={cancelRelease}
                  onConfirm={confirmRelease}
                />
              ) : null}

              {canClaimSupplierMatch(view) || canChangeClaimEmail ? (
                <div className="border-border border-t pt-4">
                  {emailFormOpen ? (
                    <SupplierMatchEmailForm
                      isSubmitting={claimMatch.isPending}
                      error={requestError}
                      onSubmit={handleClaim}
                      initialEmail={hasClaimOnFile ? "" : loginEmail}
                      suggestions={suggestions}
                      excludeEmails={hasClaimOnFile ? claimExcludes : undefined}
                      inputLabel="Trade account email"
                      submitLabel={
                        hasClaimOnFile ? "Update email" : "Claim account"
                      }
                      submittingLabel="Saving…"
                    />
                  ) : (
                    <Button
                      ref={primaryActionRef}
                      type="button"
                      variant={hasClaimOnFile ? "ghost" : "outline"}
                      size="sm"
                      onClick={() => setEmailFormOpen(true)}
                    >
                      {hasClaimOnFile
                        ? "Use a different email"
                        : "I have an account"}
                    </Button>
                  )}
                </div>
              ) : null}
            </>
          ) : (
            <>
              {verifiedEmail ? (
                <div>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    Matched via
                  </p>
                  <p
                    className={`text-sm font-medium text-gray-900 dark:text-white ${PII_MASK_CLASS}`}
                  >
                    {verifiedEmail}
                  </p>
                </div>
              ) : null}

              {pendingEmail ? (
                <PendingEmailNotice
                  email={pendingEmail}
                  finish="finish matching."
                />
              ) : null}

              {disabled ? (
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  This supplier can&apos;t be checked right now.
                </p>
              ) : null}

              <div className="flex gap-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => retryMatch.mutate(view.seller_id)}
                  disabled={disabled || retryMatch.isPending}
                >
                  {retryMatch.isPending ? "Checking…" : "Retry"}
                </Button>
                {view.status !== "released" && !confirmingRelease ? (
                  <Button
                    ref={releaseTriggerRef}
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => setConfirmingRelease(true)}
                    disabled={disabled}
                  >
                    Release
                  </Button>
                ) : null}
              </div>

              {confirmingRelease ? (
                <ReleaseConfirmation
                  prompt="Stop using this match?"
                  confirmLabel="Yes, release"
                  pendingLabel="Releasing…"
                  isPending={releaseMatch.isPending}
                  onCancel={cancelRelease}
                  onConfirm={confirmRelease}
                />
              ) : null}

              <div className="border-border border-t pt-4">
                {emailFormOpen ? (
                  <SupplierMatchEmailForm
                    isSubmitting={requestEmail.isPending}
                    error={requestError}
                    onSubmit={handleRequestEmail}
                    suggestions={suggestions}
                    excludeEmails={lookupExcludes}
                  />
                ) : (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => setEmailFormOpen(true)}
                    disabled={disabled}
                  >
                    Use a different email
                  </Button>
                )}
              </div>
            </>
          )}
        </div>

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
          >
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
